import { compose, find, get, includes, reject } from 'lodash/fp';
import { createSelector } from 'reselect';
import { createAction } from '@mews/framework';
import {
    getAppConfiguration,
    ProductChargingMode,
    ProductPostingMode,
    ProductPricing,
} from '@mews-commander/data-access';
import { ApplicationState, Product } from '@mews-commander/types';
import { getStringProp } from '@mews/fp';
import { getImageUrlOrNull } from '@mews-commander/utils';
import moduleSelector from '../selector';
import prefix from '../prefix';
import { createEntitiesReducer, createBaseEntitySelectors } from './utils';

export const NAME = 'products';

const PRODUCTS_LOADED = prefix('PRODUCTS_LOADED');

export const setProducts = (products: Product[]) => createAction(PRODUCTS_LOADED, { entities: products });

export const productsReducer = createEntitiesReducer({
    FETCH_SUCCESS: PRODUCTS_LOADED,
});

export const [getProducts, getProductsByIds, getProductByOwnerId, getProduct] = createBaseEntitySelectors<
    ApplicationState,
    Product,
    typeof NAME
>(moduleSelector, NAME);

export const getOrderableProducts = createSelector(
    getProducts,
    reject<Product>(({ isDefault, isDeleted }) => isDefault || isDeleted)
);

export const getProductNames = createSelector(getProduct, (product) => product?.names);
export const isProductPostedByTimeUnit = compose(
    (product) => product?.postingMode === ProductPostingMode.PerTimeUnit,
    getProduct
);

export const isProductChargedByPerson = compose(
    (product) =>
        includes(product?.chargingMode, [ProductChargingMode.PerPerson, ProductChargingMode.PerPersonPerTimeUnit]),
    getProduct
);

export const getProductImageUrl = createSelector(getProduct, getAppConfiguration, (product, { imageBaseUrl }) =>
    getImageUrlOrNull(imageBaseUrl)({ imageId: product?.imageId })
);

export const getServiceDefaultProduct = createSelector(getProducts, getStringProp('serviceId'), (products, serviceId) =>
    find({ isDefault: true, serviceId }, products)
);

export const getProductCurrency = createSelector(getProduct, (product) =>
    product?.pricing.discriminator === ProductPricing.Absolute ? product?.pricing.value.currency : ''
);

export const getServiceDefaultProductId = compose(get('id'), getServiceDefaultProduct);

export const isDefaultProduct = createSelector(getProduct, get('isDefault'));
